<script setup lang="ts">
import FormLabel from '@/components/FormLabel/FormLabel.vue';
import {
  MINUTES_PER_HOUR,
  formatHoursMinutes,
  splitHoursMinutes,
  toSeconds,
} from '@/utils/durationUtils';
import { t } from 'i18next';
import { computed, ref, useAttrs, watch } from 'vue';
import FormInputDisplay from '../display/FormInputDisplay.vue';
import NumberField from '../NumberField/NumberField.vue';
import type { DurationFieldProps } from '../types';

/**
 * Edits a duration that is stored in seconds as whole hours and minutes.
 * Invalid input (negative values, minutes outside 0-59) is emitted as `NaN`,
 * so that the surrounding form validation rejects it, like `NumberField` does
 * for non-numeric input.
 */

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(defineProps<DurationFieldProps>(), {
  mode: 'edit',
});

const emits = defineEmits<{
  'update:modelValue': [seconds: number | undefined];
}>();

const passdownProps = computed(() => ({
  ...props,
  label: undefined,
  infoMessage: undefined,
  modelValue: undefined,
  errorMessage: undefined,
}));

const attrs = useAttrs();
const passdownAttrs = computed(() => ({
  ...attrs,
  class: undefined,
}));

const hours = ref<number | undefined>();
const minutes = ref<number | undefined>();

// Remember what was emitted last, so that the echo of our own update does not
// overwrite what the user is typing (e.g. an invalid value that became `NaN`).
let lastEmitted: number | undefined;

watch(
  () => props.modelValue,
  (modelValue) => {
    if (Object.is(modelValue, lastEmitted)) {
      return;
    }
    const split = splitHoursMinutes(modelValue);
    hours.value = split.hours;
    minutes.value = split.minutes;
  },
  { immediate: true },
);

const isValidHours = (value?: number) =>
  value === undefined || (Number.isInteger(value) && value >= 0);
const isValidMinutes = (value?: number) =>
  value === undefined || (Number.isInteger(value) && value >= 0 && value < MINUTES_PER_HOUR);

const fieldErrors = computed(() => {
  const errorMessage = props.errorMessage;
  if (!errorMessage) return {};

  const hoursInvalid = !isValidHours(hours.value);
  const minutesInvalid = !isValidMinutes(minutes.value);

  // Only mark the half that is wrong. A message that is not caused by the
  // input itself (e.g. from the backend) is shown on both halves.
  if (hoursInvalid || minutesInvalid) {
    return {
      hours: hoursInvalid ? errorMessage : undefined,
      minutes: minutesInvalid ? t('Validation.MinutesOutOfRange') : undefined,
    };
  }
  return { hours: errorMessage, minutes: errorMessage };
});

function emitValue() {
  const value =
    isValidHours(hours.value) && isValidMinutes(minutes.value)
      ? toSeconds(hours.value, minutes.value)
      : NaN;
  lastEmitted = value;
  emits('update:modelValue', value);
}

function updateHours(value: number | undefined) {
  hours.value = value;
  emitValue();
}

function updateMinutes(value: number | undefined) {
  minutes.value = value;
  emitValue();
}

const displayValue = computed(() =>
  props.modelValue === undefined ? undefined : formatHoursMinutes(props.modelValue),
);
</script>

<template>
  <div :class="$attrs.class">
    <FormLabel :label="label" :infoMessage="infoMessage" :disabled="props.disabled">
      <FormInputDisplay v-if="mode === 'details'" :value="displayValue" />

      <v-row v-else density="compact">
        <v-col>
          <NumberField
            v-bind="{ ...passdownProps, ...passdownAttrs }"
            :modelValue="hours"
            :errorMessage="fieldErrors.hours"
            suffix="h"
            :data-testid="`${label}_Hours`"
            @update:modelValue="updateHours"
          />
        </v-col>
        <v-col>
          <NumberField
            v-bind="{ ...passdownProps, ...passdownAttrs }"
            :modelValue="minutes"
            :errorMessage="fieldErrors.minutes"
            suffix="min"
            :data-testid="`${label}_Minutes`"
            @update:modelValue="updateMinutes"
          />
        </v-col>
      </v-row>
    </FormLabel>
  </div>
</template>
